import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { ReduxRawState } from './utils/ReduxRawState';
import { computed, makeObservable } from 'mobx';

/*
customers:
    parameters:
        competition: "84"
        region: "domestic"
        sport: "football"
        tab: "telebet"
    value: "5015"
*/

const RouterValueIO = t.union([t.number, t.string, t.null /*, t.boolean*/]);

export type RouterValueType = t.TypeOf<typeof RouterValueIO>;

const RouterParametersIO = t.record(t.string, RouterValueIO);

export type RouterParametersType = t.TypeOf<typeof RouterParametersIO>;

const RouterStateItemIO = t.union([
    t.interface({
        parameters: RouterParametersIO,
        value: RouterValueIO,
    }),
    t.null,
]);

const isRouterStateItem = createGuard(RouterStateItemIO);

const RouterStateIO = t.record(t.string, RouterStateItemIO);

const RecordIO = t.record(t.string, t.unknown);
const isRecord = createGuard(RecordIO);

type RouterStateType = t.TypeOf<typeof RouterStateIO>;

export class ReduxRouterState {
    private prevData: RouterStateType | null = null;

    public constructor(private readonly reduxStore: ReduxRawState) {
        makeObservable(this);
    }

    @computed.struct public get data(): RouterStateType | null {
        const dataImmutable = this.reduxStore.getStateReactive().routes;

        if (dataImmutable === null || dataImmutable === undefined) {
            return null;
        }

        //@ts-expect-error
        if (typeof dataImmutable.toJS !== 'function') {
            return null;
        }

        //@ts-expect-error
        const data = dataImmutable.toJS();

        if (!isRecord(data)) {
            console.error('RouterReduxState -> routerData is not of the correct type', data);
            return null;
        }

        const routerDataClear: RouterStateType = {};

        for (const [key, value] of Object.entries(data)) {
            if (isRouterStateItem(value)) {
                routerDataClear[key] = value;
            } else {
                if (key === '#' && value === null) {
                    //ignore this case
                } else {
                    console.error('RouterReduxState -> routerData item is not of the correct type', key, value);
                }
            }
        }

        return routerDataClear;
    }

    public setPrevData(): void {
        this.prevData = this.data;
    }

    private getParameters(namespace: string, name: string): RouterValueType | null {
        if (this.data === null) {
            return null;
        }
        const namespaceData = this.data[namespace] ?? null;

        if (namespaceData === null) {
            return null;
        }

        return namespaceData.parameters[name] ?? null;
    }

    private getValue(namespace: string, dataProp: 'data' | 'prevData' = 'data'): RouterValueType | null {
        if (this[dataProp] === null) {
            return null;
        }

        const namespaceData = this[dataProp][namespace] ?? null;

        if (namespaceData === null) {
            return null;
        }

        return namespaceData.value;
    }

    public getValueNamespace(namespace: string): RouterValueType | null {
        return this.getValue(namespace);
    }

    public getPrevValueNamespace(namespace: string): RouterValueType | null {
        return this.getValue(namespace, 'prevData');
    }

    public getParametersAll(namespace: string): RouterParametersType {
        if (this.data === null) {
            return {};
        }
        const namespaceData = this.data[namespace] ?? null;

        if (namespaceData === null) {
            return {};
        }

        return { ...namespaceData.parameters };
    }

    public getParametersString(namespace: string, name: string): string | null {
        const value = this.getParameters(namespace, name);

        if (value === null) {
            return null;
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'number') {
            return value.toString();
        }

        if (typeof value === 'boolean') {
            console.error(`RouterReduxState -> Problem getting a string parameter ${namespace}:${name} ->`, value);
            return null;
        }

        console.error(`RouterReduxState -> Problem getting a string parameter ${namespace}:${name} ->`, value);
        return null;
    }

    public getParametersNumber(namespace: string, name: string): number | null {
        const value = this.getParameters(namespace, name);

        if (value === null) {
            return null;
        }

        if (typeof value === 'string') {
            if (value === '') {
                return null;
            }

            const valueNum = parseInt(value, 10);

            if (isNaN(valueNum)) {
                console.error(
                    `RouterReduxState -> Problem getting a numerical parameter ${namespace}:${name} ->`,
                    value
                );
                return null;
            }

            return valueNum;
        }

        if (typeof value === 'boolean') {
            console.error(`RouterReduxState -> Problem getting a numerical parameter ${namespace}:${name} ->`, value);
            return null;
        }

        return value;
    }
}
